/**
 * @fileoverview Subscriber Search API Module
 * Provides the patient/subscriber search used by the Add Patient flow.
 */

import api from "./api";

/**
 * Extracts the list of results from a subscriber-search response, tolerating
 * both a bare array and the common wrapper shapes used across this API
 * (content / data / results / subscribers).
 * @param {*} data - The response body.
 * @returns {Array<Object>} The list of results, or an empty array.
 */
const extractResults = (data) => {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.content)) return data.content;
  if (Array.isArray(data?.results)) return data.results;
  if (Array.isArray(data?.subscribers)) return data.subscribers;
  if (Array.isArray(data?.data)) return data.data;
  return [];
};

/**
 * Searches for subscribers by first name, last name, and/or subscriber number,
 * optionally filtered by gender. At least one of firstName/lastName/subscriberNum
 * must be provided (gender alone is not a valid search).
 *
 * @async
 * @function searchSubscribers
 * @param {Object} params - Search parameters
 * @param {string} [params.firstName] - Subscriber first name
 * @param {string} [params.lastName] - Subscriber last name
 * @param {string} [params.subscriberNum] - Subscriber number
 * @param {string} [params.gender] - Gender filter (e.g. "F"), matched exactly.
 * @returns {Promise<Array<{memberId: string, tdmPatientId: string, firstName: string, lastName: string, subscriberNum: string, personCode: string, gender: string, hasHaloProgram: Array<string|number>, patientAdvocate: ?{name: string}}>>}
 *   Promise resolving to a list of MemberSearchResultDTO records. `hasHaloProgram`
 *   is the list of program ids this subscriberNum+personCode already has an
 *   outreach opportunity for (empty when they have none). `patientAdvocate`
 *   is a family/subscriber-level field (present on the record(s) it applies to).
 * @throws {Error} Throws if no name/number parameter is provided or the request fails.
 */
export const searchSubscribers = async ({
  firstName,
  lastName,
  subscriberNum,
  gender,
} = {}) => {
  const params = {};
  if (firstName?.trim()) params.firstName = firstName.trim();
  if (lastName?.trim()) params.lastName = lastName.trim();
  if (subscriberNum?.trim()) params.subscriberNum = subscriberNum.trim();

  if (Object.keys(params).length === 0) {
    throw new Error(
      "At least one of firstName, lastName, or subscriberNum is required.",
    );
  }

  // Gender is an optional additional filter; it never satisfies the "at least
  // one criterion" requirement on its own.
  if (gender?.trim()) params.gender = gender.trim();

  const response = await api.get("/subscriber", {
    params,
    headers: {
      "Content-Type": "application/json",
      "ngrok-skip-browser-warning": "true",
    },
  });

  return extractResults(response?.data);
};

/**
 * Fetches the list of available programs (name + id) for the program dropdown.
 * Calls GET /outreach/filter?outreachType={type}&filterType=PROGRAM&search={name}
 *
 * @async
 * @function fetchPrograms
 * @param {Object} [options]
 * @param {string} [options.outreachType="MEMBER_OUTREACH"] - Outreach type.
 * @param {string} [options.search] - Optional name filter.
 * @returns {Promise<Array<{id: (string|number), name: string}>>} List of programs.
 */
export const fetchPrograms = async ({
  outreachType = "MEMBER_OUTREACH",
  search = "",
} = {}) => {
  const params = { outreachType, filterType: "PROGRAM" };
  if (search?.trim()) params.search = search.trim();

  const response = await api.get("/outreach/filter", {
    params,
    headers: {
      "Content-Type": "application/json",
      "ngrok-skip-browser-warning": "true",
    },
  });

  return extractResults(response?.data).map((p) => ({
    id: p.id ?? p.programId ?? p.value,
    name: p.name ?? p.programName ?? p.label ?? String(p.id ?? ""),
  }));
};

/**
 * Creates one or more members in the outreach system.
 *
 * @async
 * @function createMembers
 * @param {Array<{tdmPatientId: number, programIds: number[], useDefaultDrug?: boolean, assigneeId?: number}>} members - List of
 *   PatientOutreachDto to create. Each entry carries the patient's tdmPatientId
 *   (Long), the selected programIds (list of Long), and the submitting user's
 *   employee id as assigneeId (only applied when the member is newly created).
 * @returns {Promise<{enrolled: Object[], failed: Array<{tdmPatientId: number, programId: number, reason: string}>}>}
 *   Promise resolving to the API response: `enrolled` holds the persisted
 *   enrollments and `failed` holds per-(patient, program) failures with a
 *   human-readable `reason`. A partial success returns entries in both lists.
 * @throws {Error} Throws if the members list is empty or the request fails.
 */
export const createMembers = async (members) => {
  if (!Array.isArray(members) || members.length === 0) {
    throw new Error("At least one member is required.");
  }

  // This endpoint can echo back a member's full record - which has a
  // backend serialization bug producing a circular Member<->MemberProgram
  // structure, ballooning the response to an enormous size. When that
  // happens, the browser's native JSON parsing (used by axios's default
  // responseType: "json") silently returns null instead of throwing -
  // which we'd otherwise misread as an empty `enrolled`/`failed` response
  // and incorrectly report every member as having "no valid claims".
  // Requesting the raw text and parsing it ourselves avoids that: a
  // genuinely large-but-valid payload still parses correctly, and a
  // truly malformed/truncated one throws a real, catchable error instead
  // of failing silently.
  const response = await api.post("/outreach/members", members, {
    responseType: "text",
    headers: {
      "Content-Type": "application/json",
      "ngrok-skip-browser-warning": "true",
    },
  });

  try {
    return JSON.parse(response.data);
  } catch {
    throw new Error(
      "The server's response could not be read (it may be too large or malformed). Please try again.",
    );
  }
};

export default searchSubscribers;
